import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StatusBadge from "@/components/admin/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  Search,
  FileText,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
} from "lucide-react";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";
import NewPostDialog from "@/components/admin/NewPostDialog";
import { cn } from "@/lib/utils";

type PostRow = Database["public"]["Tables"]["posts"]["Row"];
type Status = Database["public"]["Enums"]["post_status"] | "all";
type Category = Database["public"]["Enums"]["post_category"] | "all";
type SortField = "published" | "updated";
type SortDir = "asc" | "desc";

const fmtDateOnly = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const categoryLabels: Record<string, string> = {
  article: "Article",
  insight: "Insight",
  event: "Event",
  case_study: "Case study",
};

const statusOptions: { value: Status; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const categoryOptions: { value: Category; label: string }[] = [
  { value: "all", label: "All categories" },
  { value: "article", label: "Article" },
  { value: "insight", label: "Insight" },
  { value: "event", label: "Event" },
  { value: "case_study", label: "Case study" },
];

const AdminPosts = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState<Status>("all");
  const [category, setCategory] = useState<Category>("all");
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>("published");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const column =
        sortField === "published" ? "effective_published_at" : "updated_at";
      let q = supabase
        .from("posts")
        .select("*")
        .order(column, { ascending: sortDir === "asc", nullsFirst: false });
      if (status !== "all") q = q.eq("status", status);
      if (category !== "all") q = q.eq("category", category);
      const term = debounced.trim();
      if (term) q = q.or(`title.ilike.%${term}%,slug.ilike.%${term}%`);
      const { data, error } = await q;
      if (!active) return;
      if (error)
        toast({
          title: "Failed to load posts",
          description: error.message,
          variant: "destructive",
        });
      setRows((data ?? []) as PostRow[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [status, category, debounced, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const hasFilters =
    search.trim() !== "" || status !== "all" || category !== "all";
  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setCategory("all");
  };

  const statusLabel =
    status === "all"
      ? "Status"
      : statusOptions.find((s) => s.value === status)?.label ?? "Status";
  const categoryLabel =
    category === "all"
      ? "Category"
      : categoryOptions.find((c) => c.value === category)?.label ?? "Category";

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "desc" ? (
      <ArrowDown className="w-3 h-3 text-foreground" />
    ) : (
      <ArrowUp className="w-3 h-3 text-foreground" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Posts · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <NewPostDialog
        open={newPostOpen}
        onOpenChange={setNewPostOpen}
        onCreated={(id) => {
          setNewPostOpen(false);
          navigate(`/admin/posts/${id}`);
        }}
      />

      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Posts</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {rows.length} {rows.length === 1 ? "post" : "posts"}
            {hasFilters && (
              <>
                {" · "}
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  <X className="w-3 h-3" /> Clear filters
                </button>
              </>
            )}
          </p>
        </div>
        <Button onClick={() => setNewPostOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> New post
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-[42%]">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search posts…"
                      className="pl-7 h-8 text-xs normal-case tracking-normal bg-background/60 border-border/60"
                    />
                  </div>
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                          status !== "all" && "text-foreground"
                        )}
                      >
                        {statusLabel}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {statusOptions.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => setStatus(opt.value)}
                        >
                          {opt.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                          category !== "all" && "text-foreground"
                        )}
                      >
                        {categoryLabel}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {categoryOptions.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => setCategory(opt.value)}
                        >
                          {opt.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  <button
                    onClick={() => toggleSort("published")}
                    className={cn(
                      "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                      sortField === "published" && "text-foreground"
                    )}
                  >
                    Published
                    <SortIcon field="published" />
                  </button>
                </th>
                <th className="text-left px-4 py-2 font-medium">
                  <button
                    onClick={() => toggleSort("updated")}
                    className={cn(
                      "inline-flex items-center gap-1 hover:text-foreground transition-colors",
                      sortField === "updated" && "text-foreground"
                    )}
                  >
                    Updated
                    <SortIcon field="updated" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-3"><Skeleton className="h-4 w-64" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm gap-3">
                      <FileText className="w-8 h-8 opacity-60" />
                      {hasFilters ? (
                        <>
                          <span>No posts match your filters.</span>
                          <Button variant="outline" size="sm" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        </>
                      ) : (
                        <>
                          <span>No posts yet.</span>
                          <Button size="sm" onClick={() => setNewPostOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" /> Create your first post
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/admin/posts/${r.id}`)}
                    className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-foreground">{r.title}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge
                        label={r.status === "published" ? "Published" : "Draft"}
                        tone={r.status === "published" ? "success" : "info"}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {categoryLabels[r.category]}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      {fmtDateOnly(
                        (r as any).published_at ??
                          (r as any).effective_published_at ??
                          null
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      {fmtDateOnly(r.updated_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AdminPosts;
